import { escapeHtml, discountPercent, formatMenuPriceParts } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection, MenuTheme } from '../types.js';
import {
  ARABIC_LINE,
  badge,
  bilingual,
  description,
  fitScale,
  hasTitle,
  image,
  lineCount,
  price,
  sectionSubtitle,
  sectionTitle,
  variantLabel,
  type MenuFamily,
  type MenuStyleVariant,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Chalk & Chili, layout A: a dark chalkboard pizzeria board in three columns. Each column opens
 * under a round dish photo in a double red ring, with a script heading between red bars; every
 * price sits in a red circle. Further sections in a column are drawn as red-outlined boxes with
 * their name on a pill, and the last column is framed like a specials panel. Little drawn
 * leaves, tomatoes and chilies are scattered round the photos.
 *
 * The look (chalk, rings, price circles, rows, headings) is shared with chalk-chili-hero and
 * chalk-chili-rings.
 */

/* ------------------------------------------------------------------ shared look */

const svg = (markup: string) => `url("data:image/svg+xml,${encodeURIComponent(markup)}")`;

/** Chalk dust: fine white grain over the board. */
const CHALK = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' width='360' height='360'>" +
    "<filter id='n' x='0' y='0' width='100%' height='100%'><feTurbulence type='fractalNoise' baseFrequency='.75' numOctaves='3' stitchTiles='stitch'/>" +
    "<feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 .075 0'/></filter>" +
    "<rect width='360' height='360' filter='url(#n)'/></svg>",
);

/** Small drawn garnishes: a basil leaf, a cherry tomato and a chili. */
const LEAF = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'><path d='M5 35C5 15 17 4 36 4c0 19-11 31-31 31z' fill='#4fae4a'/>" +
    "<path d='M6 34L29 11' stroke='#2f7d32' stroke-width='2.2' stroke-linecap='round'/></svg>",
);
const TOMATO = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'><circle cx='20' cy='22' r='15' fill='#e2312a'/>" +
    "<circle cx='14' cy='17' r='3.5' fill='#fff' opacity='.35'/>" +
    "<path d='M20 8l3 5 6-2-4 5 5 3-6 0-2 5-2-5-6 0 5-3-4-5 6 2z' fill='#3d9b3a'/></svg>",
);
const CHILI = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'>" +
    "<path d='M9 11c2 13 10 21 27 25-2-7-6-17-14-23-5-4-9-5-13-2z' fill='#e2312a'/>" +
    "<path d='M13 13c3 6 7 11 13 15' stroke='#fff' stroke-opacity='.35' stroke-width='2' fill='none' stroke-linecap='round'/>" +
    "<path d='M9 11c-2-2-2-5 1-7' stroke='#3d9b3a' stroke-width='3' fill='none' stroke-linecap='round'/></svg>",
);

export const CC_FAMILY: Omit<MenuFamily, 'layout'> = { id: 'chalk-chili', name: 'Chalk & Chili' };

export const CC_THEME: MenuTheme = {
  backgroundColor: '#1c1c1e',
  surfaceColor: '#29292c',
  textColor: '#f2f0ea',
  primaryColor: '#ffffff',
  accentColor: '#e3262a',
  fontId: 'cafe',
  backgroundMediaId: null,
};

export const CC_VARIANTS: MenuStyleVariant[] = [
  { id: 'red', name: 'Chalkboard & red', theme: {} },
  { id: 'orange', name: 'Chalkboard & orange', theme: { accentColor: '#f26b1d' } },
  { id: 'green', name: 'Green slate & red', theme: { backgroundColor: '#1d2a24', surfaceColor: '#283830', accentColor: '#e0352b' } },
  { id: 'brick', name: 'Brick & cream', theme: { backgroundColor: '#3a1714', surfaceColor: '#4a211c', accentColor: '#f3c34a', primaryColor: '#fff6e2' } },
];

/** Chalkboard texture and soft smudges on a design's root. */
export function ccRootCss(S: string): string {
  return `
${S}{background-color:var(--mb-bg);
  background-image:${CHALK},radial-gradient(ellipse at 18% 28%,rgba(255,255,255,.07),transparent 46%),radial-gradient(ellipse at 82% 76%,rgba(255,255,255,.05),transparent 50%);
  background-size:22em 22em,100% 100%,100% 100%;background-repeat:repeat,no-repeat,no-repeat}
${S}.mb-has-bg{background-image:none}`;
}

/** A garnish drawn at a spot the design's CSS places (`.cc-deco-<kind>`). */
export function ccDeco(kind: 'leaf' | 'tomato' | 'chili', cls: string): string {
  return `<i class="cc-deco cc-deco-${kind} ${cls}" aria-hidden="true"></i>`;
}

export function ccDecoCss(S: string): string {
  return `
${S} .cc-deco{position:absolute;z-index:0;width:2.6em;height:2.6em;background:center/contain no-repeat;pointer-events:none}
${S} .cc-deco-leaf{background-image:${LEAF}}
${S} .cc-deco-tomato{background-image:${TOMATO}}
${S} .cc-deco-chili{background-image:${CHILI}}`;
}

/** A round photo in a double accent ring; an empty ring with a chalk plate when there is none. */
export function ccRing(ctx: RenderContext, mediaId: string | null | undefined, cls: string): string {
  const img = image(ctx, mediaId, 'cc-ring-img');
  return `<div class="cc-ring ${cls}${img ? '' : ' cc-ring-empty'}">${img}</div>`;
}

export function ccRingCss(S: string): string {
  return `
${S} .cc-ring{position:relative;z-index:1;border-radius:50%;border:.42em solid var(--mb-accent);padding:.3em;background:var(--mb-bg);
  box-shadow:0 0 0 .22em var(--mb-bg),0 0 0 .38em var(--mb-accent),0 .8em 1.6em rgba(0,0,0,.55)}
${S} .cc-ring-img{display:block;width:100%;height:100%;border-radius:50%;object-fit:cover}
${S} .cc-ring-empty::after{content:'';position:absolute;inset:22%;border-radius:50%;border:.14em dashed color-mix(in srgb,var(--mb-text) 40%,transparent)}`;
}

/** A price in an accent circle: the number large, the currency small under it. */
export function ccPriceCircle(ctx: RenderContext, amount: number, cls = 'cc-pc'): string {
  const p = formatMenuPriceParts(amount, ctx.board.settings.currency);
  const number = p.fraction ? `${p.whole}.${p.fraction}` : p.whole;
  const scale = fitScale(number, 5, 0.6);
  return (
    `<span class="${cls}" dir="ltr"><span class="${cls}-n" style="--fit:${scale.toFixed(3)}">${escapeHtml(number)}</span>` +
    `<span class="${cls}-s">${escapeHtml(p.symbol)}</span></span>`
  );
}

export function ccPriceCircleCss(S: string, cls = 'cc-pc', size = 3.9): string {
  return `
${S} .${cls}{flex:0 0 auto;width:${size}em;height:${size}em;border-radius:50%;background:var(--mb-accent);color:#fff;
  display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1;box-shadow:0 .2em .5em rgba(0,0,0,.4)}
${S} .${cls}-n{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:calc(${(size * 0.34).toFixed(2)}em * var(--fit,1));white-space:nowrap}
${S} .${cls}-s{font-size:${(size * 0.17).toFixed(2)}em;font-weight:600;letter-spacing:.06em;margin-top:.15em;opacity:.9}`;
}

/** Sizes, or the struck-through original of a discounted item, under the name. */
function rowExtra(ctx: RenderContext, item: MenuItemContent): string {
  if (item.variants.length) {
    return `<p class="cc-sizes">${item.variants
      .map((v) => `<span class="cc-size"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <b dir="ltr">${price(ctx, v.price)}</b></span>`)
      .join('')}</p>`;
  }
  return discountPercent(item) !== null ? `<p class="cc-sizes"><s class="cc-old" dir="ltr">${price(ctx, item.price)}</s></p>` : '';
}

/** Name in tall capitals, description under it, the price in a red circle beside. */
export function ccRow(ctx: RenderContext, item: MenuItemContent): string {
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  const name = arabicFirst
    ? `<span class="cc-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</span>`
    : `<span class="cc-name">${escapeHtml(item.title)}</span>`;
  const second =
    ctx.showEn && ctx.showAr && item.titleAr?.trim()
      ? `<p class="cc-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`
      : '';
  const badges = badge(ctx, item, 'cc-badge');
  const circle = item.variants.length ? '' : ccPriceCircle(ctx, item.discountPrice ?? item.price);
  return [
    '<div class="cc-row"><div class="cc-txt">',
    `<div class="cc-nl">${name}${badges ? `<span class="cc-badges">${badges}</span>` : ''}</div>`,
    second,
    rowExtra(ctx, item),
    description(ctx, item, 'cc-desc'),
    `</div>${circle}</div>`,
  ].join('');
}

/** Script heading between red bars, its subtitle under it. */
export function ccHeading(ctx: RenderContext, b: MenuBlock): string {
  if (!hasTitle(b.section)) return '';
  const sub = b.continued ? '' : sectionSubtitle(ctx, b.section, 'cc-sub');
  return `<div class="cc-head"><div class="cc-hl"><i class="cc-bars"></i><div class="cc-hts">${sectionTitle(ctx, b.section, 'cc-ht')}</div><i class="cc-bars"></i></div>${sub}</div>`;
}

/** A section as a red-outlined box with its name on a pill across the top edge. */
export function ccBox(ctx: RenderContext, b: MenuBlock): string {
  const s = b.section;
  const en = s.title?.trim();
  const ar = s.titleAr?.trim();
  const parts = [ctx.showEn ? en : null, ctx.showAr ? ar : null].filter(Boolean) as string[];
  const label = parts.length ? parts.join(' · ') : en || ar || '';
  const tab = label ? `<p class="cc-tab" dir="auto" style="font-size:${(1.9 * fitScale(label, 20, 0.65)).toFixed(3)}em">${escapeHtml(label)}</p>` : '';
  return `<div class="cc-box${tab ? '' : ' cc-box-untitled'}">${tab}${b.items.map((i) => ccRow(ctx, i)).join('')}</div>`;
}

/** The first section of a column under a heading, any further ones boxed. */
export function ccBlocks(ctx: RenderContext, blocks: MenuBlock[]): string {
  return blocks
    .map((b, i) => (i === 0 ? `<div class="cc-sec">${ccHeading(ctx, b)}${b.items.map((it) => ccRow(ctx, it)).join('')}</div>` : ccBox(ctx, b)))
    .join('');
}

/** Height of a row in em, matching ccRowsCss. */
export function ccRowHeight(board: MenuBoardContent, item: MenuItemContent, perLine: number): number {
  const en = board.settings.language !== 'ar';
  const ar = board.settings.language !== 'en';
  const name = 1.5 * 1.2;
  const second = en && ar && item.titleAr?.trim() ? 1.15 * 1.3 * ARABIC_LINE : 0;
  const extra = item.variants.length || discountPercent(item) !== null ? 1.05 * 1.45 : 0;
  const lines = board.settings.showDescriptions
    ? (en || !item.descriptionAr ? lineCount(item.description, perLine, 2) : 0) +
      (ar ? lineCount(item.descriptionAr, perLine, 2) * ARABIC_LINE : 0)
    : 0;
  const text = name + second + extra + (lines ? 0.25 + lines * 0.95 * 1.3 : 0);
  return Math.max(text, 3.9) + 1.1;
}

/** Height of a section's heading or box frame in em: whichever a block may be drawn as. */
export function ccHeadHeight(board: MenuBoardContent, section: MenuSection, continued: boolean): number {
  if (!hasTitle(section)) return 1.2;
  const both = board.settings.language === 'both' && section.title?.trim() && section.titleAr?.trim();
  const heading = 3.2 * 1.2 + (both ? 2 * 1.3 * ARABIC_LINE : 0) + 1;
  const sub = !continued && (section.subtitle?.trim() || section.subtitleAr?.trim()) ? 1.1 * 1.45 + 0.2 : 0;
  const box = 2.4 + 2.6 + 0.8;
  return Math.max(heading + sub, box);
}

export function ccColumn(board: MenuBoardContent, capacity: number, perLine: number): MenuColumn {
  return {
    capacity,
    blockCost: (section, items, continued) =>
      ccHeadHeight(board, section, continued) + items.reduce((sum, i) => sum + ccRowHeight(board, i, perLine), 0) + 0.6,
  };
}

export function ccRowsCss(S: string): string {
  return `
${ccPriceCircleCss(S)}
${S} .cc-sec{display:flex;flex-direction:column;margin-bottom:.6em}
${S} .cc-head{text-align:center;margin-bottom:1em}
${S} .cc-hl{display:flex;align-items:center;justify-content:center;gap:.9em}
${S} .cc-hts{min-width:0;max-width:80%}
${S} .cc-ht{font-family:var(--mb-font-script);color:var(--mb-primary);font-size:3.2em;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding:0 .1em}
${S} .cc-ht.mb-2nd{font-size:2em;line-height:1.3;color:var(--mb-accent)}
${S} .cc-bars{flex:0 0 2.2em;height:1em;background:linear-gradient(var(--mb-accent),var(--mb-accent)) 0 0/100% .2em no-repeat,
  linear-gradient(var(--mb-accent),var(--mb-accent)) 0 50%/75% .2em no-repeat,linear-gradient(var(--mb-accent),var(--mb-accent)) 0 100%/100% .2em no-repeat}
${S} .cc-hl .cc-bars:last-child{transform:scaleX(-1)}
${S} .cc-sub{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.1em;letter-spacing:.18em;text-transform:uppercase;color:var(--mb-accent);
  margin-top:.2em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .cc-sub.mb-ar{letter-spacing:0}
${S} .cc-box{position:relative;border:.18em solid var(--mb-accent);border-radius:1.2em;padding:2.2em 1.2em .3em;margin:1.6em 0 .6em}
${S} .cc-box-untitled{padding-top:1.2em}
${S} .cc-tab{position:absolute;top:0;left:50%;transform:translate(-50%,-55%);max-width:86%;padding:.05em .9em .1em;border:.1em solid var(--mb-accent);border-radius:99em;
  background:var(--mb-bg);font-family:var(--mb-font-script);font-size:1.9em;line-height:1.25;color:var(--mb-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S}.mb-has-bg .cc-tab{background:var(--mb-surface)}
${S} .cc-row{display:flex;align-items:center;gap:.9em;margin-bottom:1.1em}
${S} .cc-txt{flex:1 1 auto;min-width:0}
${S} .cc-nl{display:flex;align-items:center;gap:.5em;min-width:0}
${S} .cc-name{min-width:0;font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.5em;line-height:1.2;letter-spacing:.05em;text-transform:uppercase;
  color:var(--mb-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .cc-name.mb-ar{letter-spacing:0}
${S} .cc-name2{font-weight:700;font-size:1.15em;line-height:1.3;color:var(--mb-primary);opacity:.85;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .cc-badges{display:flex;gap:.3em;flex-shrink:0}
${S} .cc-badge{font-size:.75em;font-weight:800;padding:.15em .55em;border-radius:99em;background:var(--mb-accent);color:#fff;white-space:nowrap}
${S} .cc-sizes{display:flex;flex-wrap:wrap;gap:.2em 1em;font-size:1.05em;line-height:1.45;color:var(--mb-accent)}
${S} .cc-size{white-space:nowrap}
${S} .cc-size small{font-size:.8em;color:var(--mb-text);opacity:.8;margin-inline-end:.25em}
${S} .cc-old{opacity:.7;color:var(--mb-text)}
${S} .cc-desc{font-size:.95em;line-height:1.3;opacity:.8;margin-top:.25em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
`;
}

/** Brand in script, the title's last word in script after tall capitals, and the tagline. */
export function ccHeader(ctx: RenderContext, cls: string, maxChars: number): string {
  const { board } = ctx;
  const title = (board.settings.title ?? board.name ?? '').trim();
  const words = title.split(/\s+/).filter(Boolean);
  const scale = fitScale(title, maxChars, 0.5);
  const titleEn =
    ctx.showEn || !board.settings.titleAr?.trim()
      ? `<p class="${cls}-title">${words.length > 1 ? `<span class="${cls}-caps">${escapeHtml(words.slice(0, -1).join(' '))}</span> ` : ''}` +
        `<span class="${cls}-script">${escapeHtml(words[words.length - 1] ?? '')}</span></p>`
      : '';
  const titleAr =
    ctx.showAr && board.settings.titleAr?.trim()
      ? `<p class="${cls}-title ${cls}-title-ar mb-ar${titleEn ? ' mb-2nd' : ''}" dir="rtl" lang="ar">${escapeHtml(board.settings.titleAr.trim())}</p>`
      : '';
  return [
    `<div class="${cls}">`,
    bilingual(ctx, board.brand.name, board.brand.nameAr, `${cls}-brand`),
    `<div class="${cls}-titles" style="font-size:${scale.toFixed(3)}em">${titleEn}${titleAr}</div>`,
    bilingual(ctx, board.brand.tagline, board.brand.taglineAr, `${cls}-tag`),
    '</div>',
  ].join('');
}

export function ccHeaderCss(S: string, cls: string, titleEm: number): string {
  return `
${S} .${cls}{position:relative;z-index:1;min-width:0}
${S} .${cls}-brand{font-family:var(--mb-font-script);color:var(--mb-accent);font-size:2em;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${cls}-brand.mb-2nd{font-size:1.5em;margin-bottom:.3em}
${S} .${cls}-title{font-size:${titleEm}em;line-height:1.15;color:var(--mb-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${cls}-caps{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);text-transform:uppercase;letter-spacing:.04em}
${S} .${cls}-script{font-family:var(--mb-font-script);font-size:1.12em;text-transform:capitalize}
${S} .${cls}-title-ar{font-family:var(--mb-font-script);line-height:1.35}
${S} .${cls}-title-ar.mb-2nd{font-size:${(titleEm * 0.5).toFixed(2)}em;color:var(--mb-accent)}
${S} .${cls}-tag{font-size:1.1em;font-weight:600;letter-spacing:.2em;text-transform:uppercase;opacity:.85;margin-top:.3em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .${cls}-tag.mb-ar{letter-spacing:0}
`;
}

/** Height of ccHeader in em. */
export function ccHeaderHeight(board: MenuBoardContent, titleEm: number): number {
  const both = board.settings.language === 'both';
  const brand = board.brand.name || board.brand.nameAr ? 2 * 1.3 + (both && board.brand.nameAr ? 1.5 * 1.3 * ARABIC_LINE : 0) : 0;
  const title = titleEm * 1.15 * 1.1 + (both && board.settings.titleAr ? titleEm * 0.5 * 1.35 * ARABIC_LINE : 0);
  const tag = board.brand.tagline || board.brand.taglineAr ? 0.3 + 1.1 * 1.4 * (both && board.brand.taglineAr ? 2.2 : 1) : 0;
  return brand + title + tag;
}

/* ------------------------------------------------------------------ layout A */

const S = '.mb-t-chalk-chili-trio';
/** Room the photo ring takes above each column. */
const PHOTO = 16.5;
const PORTRAIT_PHOTO = 18.5;
/** Portrait draws the columns larger, so two of them fill the tall screen. */
const PORTRAIT_SCALE = 1.1;

function hasPhotos(board: MenuBoardContent): boolean {
  return board.items.some((i) => i.imageMediaId) || board.sections.some((s) => s.imageMediaId);
}

function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const photo = hasPhotos(board) ? (portrait ? PORTRAIT_PHOTO : PHOTO) : 0;
  const height = portrait ? (120 - 5) / PORTRAIT_SCALE - photo : 67.5 - 5 - photo;
  const count = portrait ? 2 : 3;
  const perLine = portrait ? 38 : 44;
  // The last column is framed: its padding and frame take a little room.
  return Array.from({ length: count }, (_, i) => ccColumn(board, height - (i === count - 1 ? 1.8 : 0), perLine));
}

/** The photo above a column: its first section's picture, else its first dish with a photo. */
function columnPhoto(blocks: MenuBlock[]): string | null {
  return (
    blocks.find((b) => !b.continued && b.section.imageMediaId)?.section.imageMediaId ??
    blocks.flatMap((b) => b.items).find((i) => i.imageMediaId)?.imageMediaId ??
    null
  );
}

export const chalkChiliTrio: MenuTemplate = {
  id: 'chalk-chili-trio',
  name: 'Chalk & Chili · Three columns',
  description:
    'A chalkboard pizzeria board in three columns, each under a round dish photo in a red ring: script headings between red bars, prices in red circles, extra sections in red-outlined boxes and the last column framed as a specials panel.',
  defaultTheme: CC_THEME,
  variants: CC_VARIANTS,
  cuisines: ['pizza', 'pasta', 'burgers'],
  family: { ...CC_FAMILY, layout: 'Three columns' },
  uses: { featured: false, sectionImages: true, brand: false, decorations: [] },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), columns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const photos = hasPhotos(ctx.board);
    const cols = ctx.page.columns;
    const decos: ['leaf' | 'tomato' | 'chili', string][] = [['leaf', 'cc-d1'], ['tomato', 'cc-d2'], ['chili', 'cc-d3'], ['leaf', 'cc-d4']];
    const body = cols
      .map((blocks, i) => {
        const last = i === cols.length - 1;
        const ring = photos ? `<div class="cc1-top">${ccRing(ctx, columnPhoto(blocks), 'cc1-ring')}${ccDeco(...decos[i % 4]!)}${ccDeco(...decos[(i + 2) % 4]!)}</div>` : '';
        return `<div class="cc1-col${last ? ' cc1-frame' : ''}${photos ? '' : ' cc1-nophoto'}">${ring}<div class="cc1-body">${ccBlocks(ctx, blocks)}</div></div>`;
      })
      .join('');
    return `<div class="cc1-grid">${body}</div>`;
  },

  css: `
${ccRootCss(S)}
${S}{padding:2.5em 3em}
${ccDecoCss(S)}
${ccRingCss(S)}
${ccRowsCss(S)}
${S} .cc1-grid{flex:1;min-height:0;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));column-gap:3.5em}
${S}.mb-portrait .cc1-grid{font-size:${PORTRAIT_SCALE}em;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:2.6em}
${S} .cc1-col{position:relative;min-width:0;min-height:0;display:flex;flex-direction:column}
${S} .cc1-top{position:relative;flex:0 0 ${PHOTO}em;display:flex;justify-content:center;align-items:flex-start}
${S}.mb-portrait .cc1-top{flex-basis:${PORTRAIT_PHOTO}em}
${S} .cc1-ring{width:14em;height:14em;margin-top:.4em}
${S}.mb-portrait .cc1-ring{width:16em;height:16em}
${S} .cc1-body{position:relative;z-index:1;min-height:0}
${S} .cc1-frame .cc1-body{padding:0 1.6em}
${S} .cc1-frame::before{content:'';position:absolute;z-index:0;top:7.5em;left:0;right:0;bottom:0;border:.24em solid var(--mb-accent);border-radius:1.6em;
  box-shadow:inset 0 0 0 .45em var(--mb-bg),inset 0 0 0 .6em var(--mb-accent)}
${S}.mb-portrait .cc1-frame::before{top:8.5em}
${S} .cc1-frame.cc1-nophoto::before{top:0}
${S} .cc1-frame.cc1-nophoto .cc1-body{padding-top:1.6em}
${S} .cc-d1{left:6%;top:2.5em;transform:rotate(-20deg)}
${S} .cc-d2{right:7%;top:9.5em}
${S} .cc-d3{left:9%;top:10.5em;width:2.2em;height:2.2em}
${S} .cc-d4{right:5%;top:1.5em;transform:rotate(60deg)}
`,
};
